import { DsIconProps } from '@ice-web-app/designSystem';
import { useTranslation } from '@ice-web-app/shared-hooks';
import { useCurrentUserStore } from '@ice-web-app/shared-store';
import { Language } from '@ice-web-app/shared-types';
import { setCookie } from '@ice-web-app/shared-utils';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * React hook that centralises *all* language-switching logic used across the app.
 *
 * Responsibilities:
 * 1.  Provide the user-selected language code / display name / icon (if any).
 * 2.  Expose `changeLanguage` – a function that
 *     • persists the choice through the API,
 *     • updates `i18next`,
 *     • adjusts `<html>` attributes (`lang`, `dir`),
 *     • stores the chosen direction in cookies for server-side usage,
 *     • finally reloads the page so that lazy-loaded chunks pick up the new locale.
 * 3.  Return the full list of languages the user can switch to (from the user profile).
 *
 * Consumers (components, modals, dropdowns …) only need to import this hook; they
 * no longer have to duplicate API calls or `i18n.changeLanguage` boilerplate.
 */
export interface UseLanguageReturn {
  /** ISO 639-1 language code of the currently selected language */
  currentLanguageCode?: string;
  /** Display name (localised) of the current language */
  currentLanguageDisplayName?: string;
  /** Optional icon to represent the current language */
  currentLanguageIcon?: DsIconProps['icon'];
  /** Triggers a full language switch flow (persist + reload) */
  changeLanguage: (newCode: string) => Promise<void>;
  /** All languages available to the user (profile-driven) */
  userLanguages?: Language[];
}

export const useLanguage = (): UseLanguageReturn => {
  const { currentUser } = useCurrentUserStore();
  const { i18n } = useTranslation();

  // Store only the language code – the rest can be derived from `currentUser`
  const [currentLanguageCode, setCurrentLanguageCode] = useState<string>();

  // Keep local state in sync with user profile
  useEffect(() => {
    setCurrentLanguageCode(currentUser?.userConfiguration.language);
  }, [currentUser]);

  /**
   * Persist the choice via API, update i18n & reload.
   * Wrapped in `useCallback` to provide a stable reference for consumers.
   */
  const changeLanguage = useCallback(
    async (newCode: string) => {
      if (!i18n) return;

      // 1. Persist preference server-side
      // TODO: Implement

      // 2. Update client-side i18next instance
      await i18n.changeLanguage(newCode);

      // 3. Update document attributes & cookies (direction is locale-dependent)
      document.documentElement.setAttribute('lang', newCode);
      const direction = i18n.dir(newCode);
      document.documentElement.dir = direction;
      setCookie('dir', direction);

      // 4. Hard reload so that code-splitted chunks re-evaluate with the new locale
      window.location.reload();
    },
    [i18n],
  );

  const userLanguages = currentUser?.languages;

  const currentLanguageDisplayName = useMemo(() => {
    return userLanguages?.find((lang) => lang.name === currentLanguageCode)?.displayName ?? '';
  }, [userLanguages, currentLanguageCode]);

  return {
    currentLanguageCode,
    currentLanguageDisplayName,
    // We currently use a single globe icon for all languages; adapt if per-language icons are added.
    currentLanguageIcon: 'WEBSITE',
    changeLanguage,
    userLanguages,
  };
};
